import React, { useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, Autocomplete } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppQuery } from '../../../services/appQuery';
import { assignTariffURL, getClassMasterURL, getTariffList, PackageClassMasterResponse, tariffListResponse, tarriffRateItem } from './PackageConfigHelper';
import { getRequest } from '../../../services/httpService';
import { ReactComponent as DeleteIcon } from '../../../assets/SVG/trash.svg';
import { PackageDetails } from './PackageDefinition.helper';
import { postRequest, putRequest } from '../../../services/httpService';
import { useAlert } from '../../../components/alertProvider';
import { useTranslation } from 'react-i18next';
interface TariffRateItem {
  id: number | null;
  tariff_id: number;
  package_class_id: number | null;
  rate: string;
  autoFilled?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  packageDetail?: PackageDetails | null;
  
}

const AssignTariffModal: React.FC<Props> = ({ open, onClose, packageDetail }) => {
  const { addAlert } = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  const [selectedTariffs, setSelectedTariffs] = useState<Array<{ tariff_id:number; id: number | null; name: string }>>([]);
  const [tariffRates, setTariffRates] = useState<TariffRateItem[]>([]);
  const [applyAll, setApplyAll] = useState<boolean>(false);
  const [rateErrors, setRateErrors] = useState<Record<string, string>>({});
  const [highlightTariffId, setHighlightTariffId] = useState<number | null>(null);

  const [submitPayload, setSubmitPayload] = useState<{ tariff_rates: tarriffRateItem[] } | null>(null);
  
  const { data: PackageClassMaster } = useAppQuery<PackageClassMasterResponse>(
    ['PackageClassMaster'],
    () => getRequest<PackageClassMasterResponse>(`${getClassMasterURL()}`),
    { 
      // Call when modal is open so we fetch only as needed
      enabled: open,
      staleTime: 0,
      gcTime: 0,
    }
  );

  const { data: tariffList } = useAppQuery<tariffListResponse>(
    ['tariffList'],
    () => getRequest<tariffListResponse>(`${getTariffList()}`),
    { 
      enabled: true,
      staleTime: 0,
      gcTime: 0,
    }
  );

  const { data: PackageTariff } = useAppQuery<{ tariff_rates: tarriffRateItem[] }>(
    ['getTariffs', packageDetail?.id],
    () => getRequest<{ tariff_rates: tarriffRateItem[] }>(assignTariffURL(packageDetail!.id)),
    {
      enabled: !!packageDetail?.id && open,
      staleTime: 0,
      gcTime: 0
    }
  );

  React.useEffect(() => {
    const rates = PackageTariff?.tariff_rates ?? [];
    if (!Array.isArray(rates) || rates.length === 0) return;

    setSelectedTariffs(prev => {
      const existing = new Set(prev.map(t => t.tariff_id));
      const seen = new Set<number>();
      const adds = rates
        .filter(r => {
          if (existing.has(r.tariff_id)) return false;
          if (seen.has(r.tariff_id)) return false; // de-dupe within current response
          seen.add(r.tariff_id);
          return true;
        })
        .map(r => {
          const name = (r as { tariff_name?: string }).tariff_name ?? String(r.tariff_id);
          return { tariff_id: r.tariff_id, id: r.id ?? null, name };
        });
      return adds.length ? [...prev, ...adds] : prev;
    });

    setTariffRates(prev => {
      const next = [...prev];
      rates.forEach(r => {
        const clsId = Number(r.package_class_id);
        const idx = next.findIndex(t => t.tariff_id === r.tariff_id && t.package_class_id === clsId);
        const rateStr = String(r.rate ?? '');
        if (idx >= 0) {
          next[idx] = { ...next[idx], id: r.id ?? next[idx].id ?? null, rate: rateStr };
        } else {
          next.push({ id: r.id ?? null, tariff_id: r.tariff_id, package_class_id: Number.isFinite(clsId) ? clsId : null, rate: rateStr });
        }
      });
      return next;
    });
  }, [PackageTariff]);

  // Reapply rates automatically when Apply All is toggled on or selections change
  React.useEffect(() => {
    if (!applyAll) return;
    if (!PackageClassMaster || !packageDetail?.class_rates?.length) return;
    const rateByClassName = new Map<string, string>(
      (packageDetail.class_rates || []).map(cr => [cr.class_name, String(cr.rate ?? '')])
    );
    setTariffRates(prev => {
      const next = [...prev];
      selectedTariffs.forEach(group => {
        PackageClassMaster.data.forEach(cls => {
          if (!rateByClassName.has(cls.name)) return;
          const idx = next.findIndex(t => t.tariff_id === group.tariff_id && t.package_class_id === cls.id);
          const classRate = rateByClassName.get(cls.name) ?? '';
          if (idx >= 0) {
            if (!String(next[idx].rate).trim()) {
              next[idx] = { ...next[idx], rate: classRate, autoFilled: true };
            }
          } else {
            next.push({ id: null, tariff_id: group.tariff_id, package_class_id: cls.id, rate: classRate, autoFilled: true });
          }
        });
      });
      return next;
    });
  }, [applyAll, selectedTariffs, PackageClassMaster, packageDetail]);
  
  
  
  
  
  
  const {
    isSuccess: assignIsSuccess,
    isError: assignIsError,
    error: assignError,
  } = useAppQuery(
    ['assignTariff', packageDetail?.id, submitPayload],
    () => {
      if (!submitPayload || !packageDetail?.id) return Promise.resolve(null);
      const hasExisting = (PackageTariff?.tariff_rates?.length ?? 0) > 0;
      return hasExisting
        ? putRequest(assignTariffURL(packageDetail.id), submitPayload)
        : postRequest(assignTariffURL(packageDetail.id), submitPayload);
    },
    {
      enabled: !!submitPayload && !!packageDetail?.id,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // side-effect on result
  React.useEffect(() => {
    if (!submitPayload) return;
    if (assignIsSuccess) {
      addAlert(tBilling('package_config.messages.tariff_assigned_success', { defaultValue: 'Tariff assigned successfully.' }), 'success');
      setSubmitPayload(null);
      onClose();
    } else if (assignIsError) {
      const message = assignError instanceof Error
        ? assignError.message
        : tBilling('package_config.messages.tariff_assign_failed', { defaultValue: 'Failed to assign tariff.' });
      addAlert(message, 'error');
      setSubmitPayload(null);
    }
  }, [assignIsSuccess, assignIsError, onClose, assignError, submitPayload, addAlert, tBilling]);




  const handleClose = () => {
    // Clear all local state on close
    setSelectedTariffs([]);
    setTariffRates([]);
    setRateErrors({});
    setApplyAll(false);
    setSubmitPayload(null);
    onClose();
  };

  const buildPayload = () => ({
    tariff_rates: (tariffRates || [])
      .filter((t) => t.package_class_id != null && String(t.rate).trim().length > 0)
      .map((t) => ({
        ...(t.id != null ? { id: t.id } : {}),
        tariff_id: t.tariff_id,
        package_class_id: Number(t.package_class_id),
        rate: String(t.rate),
      })),
  });

  const handleSubmit = (payload: { tariff_rates: tarriffRateItem[] }) => {
    setSubmitPayload(payload);
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }} data-testid="assign-tariff-modal">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{tBilling('package_config.apply_tariff', { defaultValue: 'Apply Tariff' })} {packageDetail?.package_name ? `(${packageDetail.package_name })` : ''}</Typography>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <Autocomplete
            fullWidth
            size="small"
            options={tariffList?.data ?? []}
            getOptionLabel={(opt) => opt?.name ?? ''}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            onChange={(_, option) => {
              if (!option) return;
              setSelectedTariffs((prev) => {
                if (prev.some((t) => t.tariff_id === option.id)) {
                  setHighlightTariffId(option.id);
                  setTimeout(() => setHighlightTariffId(null), 1200);
                  return prev;
                }
                return [...prev, { tariff_id: option.id, id: null, name: option.name }];
              });
              setTariffRates((prev) => {
                if (prev.some((t) => t.tariff_id === option.id)) return prev;
                return [...prev, { id: null, tariff_id: option.id, package_class_id: null, rate: '' }];
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tBilling('package_config.search_tariff', { defaultValue: 'Search Tariff' })}
                placeholder={tBilling('package_config.placeholders.search_tariff_and_add', { defaultValue: 'Search Tariff and Add' })}
              />
            )}
          />
        </Box>

        {selectedTariffs.map((group) => (
          <Box
            key={group.tariff_id}
            sx={{
              border: '1px solid #eee',
              borderRadius: 2,
              p: 2,
              mb: 2,
              transition: 'box-shadow 200ms ease, border-color 200ms ease',
              boxShadow: highlightTariffId === group.tariff_id ? '0 0 0 2px rgba(66, 165, 245, 0.5)' : 'none',
              borderColor: highlightTariffId === group.tariff_id ? '#42a5f5' : '#eee',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontWeight: 600 }}> {group.name}</Typography>
              <IconButton
                onClick={() => {
                  setSelectedTariffs((prev) => prev.filter((t) => t.id !== group.id));
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                overflowX: 'auto',
                pb: 1,
              }}
            >
              {PackageClassMaster && PackageClassMaster.data.map((cls) => {
                const current = tariffRates.find((t) => t.tariff_id === group.tariff_id && t.package_class_id === cls.id);
                const checked = Boolean(current);
                return (
                  <Box
                    key={cls.id}
                    sx={{
                      minWidth: 260,
                      border: '1px solid #eee',
                      borderRadius: 2,
                      p: 2,
                      flex: '0 0 auto',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setTariffRates((prev) => {
                            const next = [...prev];
                            const idx = next.findIndex((t) => t.tariff_id === group.tariff_id && t.package_class_id === cls.id);
                            if (isChecked) {
                              if (idx === -1) {
                                next.push({ id: null, tariff_id: group.tariff_id, package_class_id: cls.id, rate: '' });
                              }
                            } else if (idx >= 0) {
                              next.splice(idx, 1);
                            }
                            return next;
                          });
                        }}
                        sx={{ '&.Mui-checked': { color: '#66bb6a' } }}
                      />
                      <Typography>{cls.name}</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      type='number'
                      label={tBilling('package_config.rate', { defaultValue: 'Rate' })}
                      placeholder={tBilling('package_config.placeholders.enter_rate', { defaultValue: 'Enter Rate' })}
                      value={current?.rate ?? ''}
                      error={Boolean(rateErrors[`${group.tariff_id}-${cls.id}`])}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTariffRates((prev) => {
                          const next = [...prev];
                          const idx = next.findIndex((t) => t.tariff_id === group.tariff_id && t.package_class_id === cls.id);
                          if (idx >= 0) {
                            next[idx] = { ...next[idx], rate: val };
                          } else {
                            // If user types rate, ensure entry exists
                            next.push({ id: null, tariff_id: group.tariff_id, package_class_id: cls.id, rate: val });
                          }
                          return next;
                        });
                        // clear error for this pair when user types
                        setRateErrors((prev) => {
                          const rest = { ...prev };
                          delete rest[`${group.tariff_id}-${cls.id}`];
                          return rest;
                        });
                      }}
                      disabled={!checked}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Checkbox
            checked={applyAll}
            sx={{ '&.Mui-checked': { color: '#66bb6a' } }}
            onChange={(e) => {
              const checked = e.target.checked;
              setApplyAll(checked);
              if (checked && PackageClassMaster && packageDetail?.class_rates?.length) {
                const rateByClassName = new Map<string, string>(
                  (packageDetail.class_rates || []).map(cr => [cr.class_name, String(cr.rate ?? '')])
                );
                setTariffRates(prev => {
                  const next = [...prev];
                  // For each selected tariff, ensure all classes are added with their package rates
                  selectedTariffs.forEach(group => {
                    PackageClassMaster.data.forEach(cls => {
                      // Only act on classes present in package details
                      if (!rateByClassName.has(cls.name)) return;
                      const idx = next.findIndex(t => t.tariff_id === group.tariff_id && t.package_class_id === cls.id);
                      const classRate = rateByClassName.get(cls.name) ?? '';
                      if (idx >= 0) {
                        // Only set if empty to avoid overwriting user input
                        if (!String(next[idx].rate).trim()) {
                          next[idx] = { ...next[idx], rate: classRate, autoFilled: true };
                        }
                      } else {
                        next.push({ id: null, tariff_id: group.tariff_id, package_class_id: cls.id, rate: classRate, autoFilled: true });
                      }
                    });
                  });
                  return next;
                });
              } else if (!checked) {
                // On uncheck: remove only entries that were auto-applied by 'Apply Rate To All'
                setTariffRates(prev => prev.filter(t => !t.autoFilled));
              }
            }}
          />
          <Typography>{tBilling('package_config.apply_rate_to_all', { defaultValue: 'Apply Rate To All' })}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
          <Button variant="outlined" sx={{minWidth:100 }} onClick={handleClose}>{tBilling('package_config.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#444', '&:hover': { backgroundColor: '#222' },minWidth:100 }}
            onClick={() => {
              // Validate: for every selected class, rate must be entered
              const selectedPairs = tariffRates.filter((t) => t.package_class_id != null);
              const invalid = selectedPairs.filter((t) => !String(t.rate).trim());
              if (invalid.length > 0) {
                const errs: Record<string, string> = {};
                invalid.forEach((t) => {
                  errs[`${t.tariff_id}-${t.package_class_id}`] = tBilling('package_config.validation.rate_required', { defaultValue: 'Rate is required' });
                });
                setRateErrors(errs);
                return; // block submit
              }

              const payload = buildPayload();
              if (payload.tariff_rates.length > 0) {
                handleSubmit(payload);
              }
            }}
          >
            {tBilling('package_config.save', { defaultValue: 'Save' })}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssignTariffModal;
